import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { formatUSADate, formatUSADateTime, formatNaiveDateTime } from "../../../utils/dateFormatters";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    paddingBottom: 60, 
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    color: "#333333",
    position: "relative"
  },
  
  watermark: {
    position: "absolute",
    top: "35%",
    left: "10%",
    width: "80%",
    opacity: 0.06,
    zIndex: -1,
  },
  
  header: {
    marginBottom: 20
  },
  logo: {
    width: 130,
    height: 30,
    marginBottom: 5,
    objectFit: "contain"
  },
  headerSeparator: {
    width: 140,
    height: 2,
    backgroundColor: "#333333",
    marginBottom: 10
  },
  locationName: {
    fontSize: 11,
    color: "#888888",
    marginBottom: 5
  },
  reportTitleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    paddingBottom: 8,
    marginBottom: 15
  },
  reportTitle: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold",
    color: "#222222"
  },
  reportDate: {
    fontSize: 11,
    color: "#444444"
  },
  
  section: {
    marginBottom: 20,
  },
  sectionTitleWrapper: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10
  },
  blueBar: {
    width: 3,
    height: 15,
    backgroundColor: "#4a90e2",
    marginRight: 8
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    color: "#333333"
  },
  
  gridContainer: {
    flexDirection: "row",
    justifyContent: "space-between"
  },
  gridColumn: {
    width: "48%"
  },
  fieldGroup: {
    marginBottom: 8
  },
  label: {
    fontSize: 9,
    color: "#888888",
    marginBottom: 2,
    fontFamily: "Helvetica-Bold"
  },
  value: {
    fontSize: 10,
    color: "#000000"
  },
  
  table: {
    width: "100%",
    marginTop: 5
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#dddddd",
    paddingVertical: 6,
    paddingHorizontal: 4,
    backgroundColor: "#f8f9fa"
  },
  tableHeaderCell: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#444444",
    textAlign: "left"
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: "#eeeeee"
  },
  tableRowEven: {
    backgroundColor: "transparent"
  },
  tableRowOdd: {
    backgroundColor: "rgba(249, 251, 253, 0.7)"
  },
  tableCell: {
    fontSize: 9,
    color: "#333333",
    textAlign: "left"
  },
  emptyText: {
    color: "#999999",
    fontSize: 10,
    marginLeft: 11,
    fontStyle: "italic",
    marginTop: 5,
    textAlign: "center"
  },
  
  eventLogItem: {
    flexDirection: "row",
    paddingVertical: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: "#eeeeee"
  },
  eventLogDate: {
    width: 110,
    fontSize: 9,
    color: "#333333"
  },
  eventLogContent: {
    flex: 1
  },
  eventLogTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: "#4a90e2",
    marginBottom: 2
  },
  eventLogDetail: {
    fontSize: 9,
    color: "#444444",
    lineHeight: 1.3
  },
  eventLogCreator: {
    fontSize: 8,
    color: "#666666",
    marginTop: 2,
    fontStyle: "italic"
  },
  
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    borderTopWidth: 0.5,
    borderTopColor: '#cccccc',
    paddingTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#888888'
  }
});

const AuditReportPDF = ({
  patient,
  auditReport,
  date,
  patientName,
  formatSecondsToTime,
  formatReportDate,
  logo,
  billingData,
  assignedDevices,
  eventLog,
  auditType,
  monthlyRpmTime
}) => {
  const generationTime = new Date().toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  });

  const rpmBillings = (billingData?.rpm_billings || []).filter(b => b.status !== 'non_billable');

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {logo && <Image src={logo} style={styles.watermark} fixed />}

        {/* Header */}
        <View style={styles.header}>
          {logo && <Image src={logo} style={styles.logo} />}
          <View style={styles.headerSeparator} />
          <Text style={styles.locationName}>
            {patient?.location?.name || "_"}
          </Text>
          <View style={styles.reportTitleRow}>
            <Text style={styles.reportTitle}>
              RPM Audit Report
            </Text>
            <Text style={styles.reportDate}>{formatReportDate(date)}</Text>
          </View>
        </View>

        {/* Patient Snapshot */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Patient Snapshot</Text>
          </View>
          <View style={styles.gridContainer}>
            <View style={styles.gridColumn}>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Patient Name</Text>
                <Text style={styles.value}>{patientName}</Text>
              </View>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>MRN</Text>
                <Text style={styles.value}>{patient?.user?.id || "_"}</Text>
              </View>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Deactivated On</Text>
                <Text style={styles.value}>--</Text>
              </View>
            </View>
            <View style={styles.gridColumn}>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Patient DOB</Text>
                <Text style={styles.value}>{formatUSADate(patient?.user?.dob)}</Text>
              </View>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>ICD-10</Text>
                <Text style={styles.value}>
                  {patient?.rpm_codes?.length > 0 ? patient.rpm_codes.join(", ") : "--"}
                </Text>
              </View>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Reporting Period</Text>
                <Text style={styles.value}>{formatReportDate(date)}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Program Details */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Program Details</Text>
          </View>
          <View style={styles.gridContainer}>
            <View style={styles.gridColumn}>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Enrollment Date</Text>
                <Text style={styles.value}>{formatUSADate(auditReport?.patient?.enrollment_date)}</Text>
              </View>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>RPM Monitoring Time</Text>
                <Text style={styles.value}>
                  {monthlyRpmTime || "--"}
                </Text>
              </View>
            </View>
            <View style={styles.gridColumn}>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Consent Date</Text>
                <Text style={styles.value}>{formatUSADate(auditReport?.patient?.consent_date)}</Text>
              </View>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Days of Readings</Text>
                <Text style={styles.value}>{auditReport?.reading_days_count || 0}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Device Information</Text>
          </View>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { width: "30%" }]}>Device Type</Text>
              <Text style={[styles.tableHeaderCell, { width: "30%" }]}>Serial Number</Text>
              <Text style={[styles.tableHeaderCell, { width: "20%" }]}>Date Assigned</Text>
              <Text style={[styles.tableHeaderCell, { width: "20%" }]}>Status</Text>
            </View>
            {assignedDevices?.length > 0 ? (
              assignedDevices.map((device, index) => (
                <View key={index} style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]} wrap={false}>
                  <Text style={[styles.tableCell, { width: "30%" }]}>{device.device_type}</Text>
                  <Text style={[styles.tableCell, { width: "30%" }]}>{device.imei || device.hardware_uuid}</Text>
                  <Text style={[styles.tableCell, { width: "20%" }]}>{formatUSADate(device.assigned_at)}</Text>
                  <Text style={[styles.tableCell, { width: "20%" }]}>{device.status}</Text>
                </View>
              ))
            ) : (
              <Text style={styles.emptyText}>No assigned devices found.</Text>
            )}
          </View>
        </View>

        {/* Other Details */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Other Details</Text>
          </View>
          <View style={styles.gridContainer}>
            <View style={styles.gridColumn}>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Referring Provider</Text>
                <Text style={styles.value}>{patient?.provider?.first_name} {patient?.provider?.last_name}</Text>
              </View>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Organization</Text>
                <Text style={styles.value}>{patient?.location?.name || "--"}</Text>
              </View>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Case Manager</Text>
                <Text style={styles.value}>{patient?.case_manager?.first_name} {patient?.case_manager?.last_name}</Text>
              </View>
            </View>
            <View style={styles.gridColumn}>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Secondary Provider</Text>
                <Text style={styles.value}>{patient?.secondary_provider || "--"}</Text>
              </View>
              <View style={styles.fieldGroup}>
                <Text style={styles.label}>Supervising Provider</Text>
                <Text style={styles.value}>
                    {patient?.nurse ? `${patient.nurse.first_name} ${patient.nurse.last_name}` : "--"}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* CPT Codes Generated */}
        <View style={styles.section}>
             <View style={styles.sectionTitleWrapper}>
                <View style={styles.blueBar} />
                <Text style={styles.sectionTitle}>CPT Codes Generated</Text>
             </View>
             <View style={styles.table}>
                <View style={styles.tableHeader}>
                   <Text style={[styles.tableHeaderCell, {width: '12%'}]}>Code</Text>
                   <Text style={[styles.tableHeaderCell, {width: '8%'}]}>Units</Text>
                   <Text style={[styles.tableHeaderCell, {width: '15%'}]}>Status</Text>
                   <Text style={[styles.tableHeaderCell, {width: '65%'}]}>Description</Text>
                </View>
                {rpmBillings.length > 0 ? rpmBillings.map((bill, index) => (
                   <View key={index} style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]} wrap={false}>
                      <Text style={[styles.tableCell, {width: '12%'}]}>{bill.code}</Text>
                      <Text style={[styles.tableCell, {width: '8%'}]}>{bill.units}</Text>
                      <Text style={[styles.tableCell, {width: '15%'}]}>{bill.status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Text>
                      <Text style={[styles.tableCell, {width: '65%'}]}>{bill.description}</Text>
                   </View>
                )) : <Text style={styles.emptyText}>No CPT codes generated for this period.</Text>}
             </View>
        </View>

        {/* Monitoring Sessions */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Monitoring Sessions</Text>
          </View>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { width: "40%" }]}>Date</Text>
              <Text style={[styles.tableHeaderCell, { width: "25%" }]}>Duration</Text>
              <Text style={[styles.tableHeaderCell, { width: "35%" }]}>Staff</Text>
            </View>
            {auditReport?.monitoring_sessions?.length > 0 ? (
              auditReport.monitoring_sessions.map((session, index) => (
                <View key={index} style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]} wrap={false}>
                  <Text style={[styles.tableCell, { width: "40%" }]}>{formatNaiveDateTime(session?.created_at)}</Text>
                  <Text style={[styles.tableCell, { width: "25%" }]}>{formatSecondsToTime(auditType === "ccm" ? session?.total_ccm_seconds : session?.total_rpm_seconds)}</Text>
                  <Text style={[styles.tableCell, { width: "35%" }]}>{session?.staff_member?.full_name || session?.assigned_by_name || "--"}</Text>
                </View>
              ))
            ) : (
              <Text style={styles.emptyText}>No sessions recorded.</Text>
            )}
          </View>
        </View>

        {/* Notes */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Notes</Text>
          </View>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { width: "18%", paddingRight: 8 }]}>Date</Text>
              <Text style={[styles.tableHeaderCell, { width: "8%", paddingLeft: 8, borderLeftWidth: 1, borderLeftColor: "#e0e0e0" }]}>Type</Text>
              <Text style={[styles.tableHeaderCell, { width: "49%", paddingRight: 10 }]}>Text</Text>
              <Text style={[styles.tableHeaderCell, { width: "25%", paddingLeft: 10, borderLeftWidth: 1, borderLeftColor: "#e0e0e0" }]}>Creator</Text>
            </View>
            {(() => {
              // Filter notes based on auditType (rpm_note for RPM reports, ccm_note for CCM reports)
              const filteredNotes = auditReport?.notes?.filter(note => {
                const noteType = (note?.note_type || note?.type || '').toLowerCase();
                if (auditType === 'rpm') {
                  return noteType === 'rpm_note' || noteType === 'rpm';
                } else if (auditType === 'ccm') {
                  return noteType === 'ccm_note' || noteType === 'ccm';
                }
                return true; // Show all if no auditType specified
              }) || [];

              // Helper to format note type for display
              const formatNoteType = (noteType) => {
                if (!noteType) return "--";
                const type = noteType.toLowerCase();
                if (type === 'rpm_note' || type === 'rpm') return 'RPM';
                if (type === 'ccm_note' || type === 'ccm') return 'CCM';
                return noteType;
              };

              return filteredNotes.length > 0 ? (
                filteredNotes.map((note, index) => (
                  <View key={index} style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]} wrap={false}>
                    <Text style={[styles.tableCell, { width: "18%", paddingRight: 8 }]}>{formatUSADateTime(note?.created_at)}</Text>
                    <Text style={[styles.tableCell, { width: "8%", paddingLeft: 8, borderLeftWidth: 1, borderLeftColor: "#e0e0e0" }]}>{formatNoteType(note?.note_type || note?.type)}</Text>
                    <Text style={[styles.tableCell, { width: "49%", paddingRight: 10 }]}>{note?.note_text}</Text>
                    <Text style={[styles.tableCell, { width: "25%", paddingLeft: 10, borderLeftWidth: 1, borderLeftColor: "#e0e0e0" }]}>{note?.assigned_by_name || note?.assigned_by || "--"}</Text>
                  </View>
                ))
              ) : (
                <Text style={styles.emptyText}>No {auditType?.toUpperCase() || ''} notes available.</Text>
              );
            })()}
          </View>
        </View>

        {/* SMS Messages */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>SMS Messages</Text>
          </View>
          <Text style={styles.emptyText}>No SMS messages sent.</Text>
        </View>

        {/* Readings */}
        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Readings</Text>
          </View>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { width: "30%" }]}>Date</Text>
              <Text style={[styles.tableHeaderCell, { width: "25%" }]}>Type</Text>
              <Text style={[styles.tableHeaderCell, { width: "45%" }]}>Value</Text>
            </View>
            {auditReport?.readings?.length > 0 ? (
              auditReport.readings.map((reading, index) => (
                <View key={index} style={[styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd]} wrap={false}>
                  <Text style={[styles.tableCell, { width: "30%" }]}>{formatUSADateTime(reading?.recorded_at)}</Text>
                  <Text style={[styles.tableCell, { width: "25%" }]}>{reading?.reading_type_display || reading?.reading_type}</Text>
                  <Text style={[styles.tableCell, { width: "45%" }]}>
                    {reading.systolic || reading.diastolic
                      ? `${reading.systolic ?? 0}/${reading.diastolic ?? 0} mm Hg${reading.heart_rate ? ` / ${reading.heart_rate} bpm` : ""}`
                      : reading.weight
                      ? `${reading.weight} kg`
                      : reading.glucose
                      ? `${reading.glucose} mg/dl`
                      : reading.temperature
                      ? `${reading.temperature} F`
                      : reading.oxygen
                      ? `${reading.oxygen}% / ${reading.heart_rate} bpm`
                      : reading.value_1 || "-"}
                  </Text>
                </View>
              ))
            ) : (
              <Text style={styles.emptyText}>No readings recorded.</Text>
            )}
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionTitleWrapper}>
            <View style={styles.blueBar} />
            <Text style={styles.sectionTitle}>Event Log</Text>
          </View>
          {eventLog && eventLog.length > 0 ? (
            eventLog.map((event, index) => (
              <View key={index} style={styles.eventLogItem} wrap={false}>
                <Text style={styles.eventLogDate}>{formatNaiveDateTime(event.date)}</Text>
                <View style={styles.eventLogContent}>
                  <Text style={styles.eventLogTitle}>{event.type}</Text>
                  <Text style={styles.eventLogDetail}>{event.details}</Text>
                  {event.creator && event.creator !== "Device" && event.creator !== "--" && (
                    <Text style={styles.eventLogCreator}>by {event.creator}</Text>
                  )}
                </View>
              </View>
            ))
          ) : (
             <Text style={styles.emptyText}>No events logged.</Text>
          )}
        </View>

        <View style={styles.footer} fixed>
          <Text>{patientName} - RPM Audit Report</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} of ${totalPages}`} />
          <Text>{generationTime}</Text>
        </View>
      </Page>
    </Document>
  );
};

export default AuditReportPDF;